import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useExcluirOS } from '@/hooks/useOrdensServico';
import { OrdemServico } from '@/types/ordemServico';
import { Loader2, Trash2, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ExcluirOSDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  os: OrdemServico;
}

export function ExcluirOSDialog({ open, onOpenChange, os }: ExcluirOSDialogProps) {
  const { t } = useTranslation();
  const [motivo, setMotivo] = useState('');
  const excluirOS = useExcluirOS();
  const navigate = useNavigate();

  const handleExcluir = async () => {
    if (!motivo.trim()) return;
    
    await excluirOS.mutateAsync({
      id: os.id,
      motivo: motivo.trim(),
    });
    setMotivo('');
    onOpenChange(false);
    navigate('/ordens');
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setMotivo('');
    }
    onOpenChange(open);
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-destructive" />
            {t('orders.delete.title')}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>
                <span dangerouslySetInnerHTML={{ __html: t('orders.delete.description', { number: os.numero_os }) }} />
              </p>
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                <p className="text-sm text-destructive font-medium">
                  {t('orders.delete.warning')}
                </p>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2">
          <Label htmlFor="motivo">
            {t('orders.delete.reasonLabel')} <span className="text-destructive">*</span>
          </Label>
          <Textarea
            id="motivo"
            placeholder={t('orders.delete.reasonPlaceholder')}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={3}
          />
          {!motivo.trim() && (
            <p className="text-xs text-muted-foreground">
              {t('orders.delete.reasonRequired')}
            </p>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={excluirOS.isPending}>
            {t('common.cancel')}
          </AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={handleExcluir}
            disabled={excluirOS.isPending || !motivo.trim()}
          >
            {excluirOS.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('orders.delete.deleting')}
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                {t('orders.delete.confirm')}
              </>
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
